import React, { useState } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import HeaderBase, {
  LogoLink as LogoLinkBase,
  NavLinks,
  NavLink as NavLinkBase
} from "../headers/light.js";
import { Container as ContainerBase, ContentWithVerticalPadding, Content2Xl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import logoImageSrc from "images/cblogo.png";
import serverIllustrationImageSrc from "images/cbhall_front.jpg";
import heroBackgroundImageSrc from "images/hall_pan_5.jpg";

const PrimaryBackgroundContainer = styled.div`
  ${tw`-mx-8 px-8 text-brand5`}
  background-image: linear-gradient(rgba(250, 249, 249, 0.28), rgba(250, 249, 249, 0.28)), url(${heroBackgroundImageSrc});
  background-size: cover;
  background-position: center;
`;
const Header = tw(HeaderBase)`max-w-none -mt-4 py-8 -mx-4 px-8`;
const NavLink = tw(NavLinkBase)`text-lg lg:text-xl font-bold lg:text-brand5 lg:hover:text-brand4 lg:focus:text-brand4 border-0`;
const LogoLink = tw(LogoLinkBase)`text-brand5 hocus:text-brand4`;

const Container = tw(ContainerBase)`px-4`;
const Row = tw.div`flex items-center flex-col lg:flex-row`;
const Column = tw.div`lg:w-1/2`;
const TextColumn = tw.div`text-center lg:text-left`;
const IllustrationColumn = tw(Column)`mt-16 lg:mt-0 lg:ml-16`;
const Heading = tw(SectionHeading)`max-w-3xl lg:max-w-4xl text-2xl sm:text-3xl lg:text-4xl lg:text-left leading-tight`;
const Description = tw(SectionDescription)`mt-4 max-w-2xl text-brand5 lg:text-base mx-auto lg:mx-0 font-bold`;
const PrimaryButton = tw(PrimaryButtonBase)`text-sm sm:text-base px-6 py-5 sm:px-10 sm:py-5 bg-brand5 inline-block rounded-full hocus:bg-brand4 text-brand1 font-bold`;
const ButtonRow = tw.div`mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start`;
const SecondaryButton = tw.button`mt-4 sm:mt-0 sm:ml-4 text-sm sm:text-base px-6 py-5 sm:px-10 sm:py-5 bg-brand5 inline-block rounded-full hocus:bg-brand4 text-brand1 font-bold`;
const ModalOverlay = tw.div`fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand5 bg-opacity-25`;
const ModalPanel = tw.div`relative w-full max-w-xl rounded-2xl bg-brand2 p-8 shadow-lg`;
const ModalCloseButton = tw.button`absolute top-0 right-0 mt-4 mr-4 text-3xl leading-none text-brand4 hocus:text-brand5`;
const ModalHeading = tw.h3`text-4xl font-bold text-brand5`;
const ModalText = tw.p`mt-3 text-lg text-brand5`;
const MailingListForm = tw.form`mt-6`;
const MailingListInput = tw.input`w-full border border-brand3 rounded-xl bg-brand1 text-brand5 placeholder-brand4 px-4 py-3 text-xl focus:outline-none focus:border-brand4`;
const MailingListSubmitButton = tw.button`w-full mt-4 rounded-xl bg-brand5 text-brand1 py-4 text-2xl font-bold transition duration-300 hocus:bg-brand4`;
const StatusText = tw.p`mt-4 text-base text-brand4`;
const ErrorText = tw.p`mt-4 text-base text-brand5`;
const Image = tw.img`w-144 ml-auto rounded-2xl`
const encode = (data) =>
  Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");

const TwoColPrimaryBkround  = ({
  heading = "At the heart of our village, bringing people together for events, activities, and community spirit.",
  description = "Available for booking now.",
  primaryButtonText = "Book Now",
  primaryButtonUrl = "/book",
  imageSrc = serverIllustrationImageSrc,
}) => {
  const [isMailingListOpen, setIsMailingListOpen] = useState(false);
  const [mailingListStatus, setMailingListStatus] = useState("");
  const [mailingListError, setMailingListError] = useState("");

  const openMailingList = () => {
    setMailingListStatus("");
    setMailingListError("");
    setIsMailingListOpen(true);
  };

  const closeMailingList = () => {
    setMailingListStatus("");
    setMailingListError("");
    setIsMailingListOpen(false);
  };

  const submitMailingList = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    if (process.env.NODE_ENV === "development") {
      setMailingListStatus("Form configured. Netlify submissions will appear after deployment.");
      setMailingListError("");
      form.reset();
      return;
    }

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(data)
      });

      if (!response.ok) {
        throw new Error("Unable to submit mailing list form");
      }

      setMailingListStatus("Thanks for joining the mailing list!");
      setMailingListError("");
      form.reset();
    } catch (error) {
      console.log(error);
      setMailingListError("Sorry, something went wrong. Please try again.");
      setMailingListStatus("");
    }
  };
  const logoLink = (
    <LogoLink href="/">
      <img src={logoImageSrc} alt="Logo" />
      Clarebrand Village Hall
    </LogoLink>
  );
  const navLinks = [
    <NavLinks key={1}>
      <NavLink href="/">Home</NavLink>
      <NavLink href="/#nav-our-values">Our Values</NavLink>
      <NavLink href="/#nav-contact-us">Contact Us</NavLink>
    </NavLinks>
  ];
  return (
    <PrimaryBackgroundContainer>
      <Content2Xl>
        <Header logoLink={logoLink} links={navLinks} />
        <Container>
          <ContentWithVerticalPadding>
            <Row>
              <TextColumn>
                <Heading>{heading}</Heading>
                <Description>{description}</Description>
                <ButtonRow>
                  <PrimaryButton as="a" href={primaryButtonUrl}>{primaryButtonText}</PrimaryButton>
                  <SecondaryButton type="button" onClick={openMailingList}>Join Mailinglist</SecondaryButton>
                </ButtonRow>
              </TextColumn>
              <IllustrationColumn>
                <Image src={imageSrc} title="Clarebrand Hall" />
              </IllustrationColumn>
            </Row>
          </ContentWithVerticalPadding>
        </Container>
        {isMailingListOpen && (
          <ModalOverlay onClick={closeMailingList}>
            <ModalPanel onClick={(e) => e.stopPropagation()}>
              <ModalCloseButton type="button" onClick={closeMailingList} aria-label="Close mailing list popup">
                ×
              </ModalCloseButton>
              <ModalHeading>Join Mailinglist</ModalHeading>
              <ModalText>Enter your email address and we’ll keep you updated.</ModalText>
              <MailingListForm name="mailing-list" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={submitMailingList}>
                <input type="hidden" name="form-name" value="mailing-list" />
                <input type="hidden" name="bot-field" />
                <MailingListInput type="email" name="email" placeholder="you@example.com" required />
                <MailingListSubmitButton type="submit">Join Mailinglist</MailingListSubmitButton>
              </MailingListForm>
              {mailingListStatus && <StatusText>{mailingListStatus}</StatusText>}
              {mailingListError && <ErrorText>{mailingListError}</ErrorText>}
            </ModalPanel>
          </ModalOverlay>
        )}
      </Content2Xl>
    </PrimaryBackgroundContainer>
  );
};

export default TwoColPrimaryBkround;
